import { z } from 'zod';
import { OpaqueRef } from './ids.js';
import { IsoDateTime, PositiveCents } from './primitives.js';
import { Slot } from './slot.js';
/**
 * A short-lived claim on a slot, so two guests are not sold the same 2 PM while
 * one of them is still deciding. Expiry is enforced by every query filtering on
 * `expires_at > now()`; the sweeper is a convenience, not the mechanism.
 */
export const Hold = z.object({
    hold_id: OpaqueRef,
    expires_at: IsoDateTime,
});
/**
 * Statuses that occupy the room. Only these count as `busy` in the availability
 * engine, which is what makes a cancellation return the slot to the book.
 */
export const LIVE_BOOKING_STATUSES = ['confirmed', 'completed'];
export const BookingStatus = z.enum(['confirmed', 'completed', 'cancelled', 'no_show']);
/**
 * **`requested` is not a `BookingStatus`, and the absence is the guarantee.**
 *
 * A5 (`docs/lovespa-gaps-plan.md`) gives the book a fifth live status: an
 * appointment a guest asked for and the owner has not confirmed. It occupies a
 * resource, it appears on her calendar, it expires — everything a booking does
 * except *be one*.
 *
 * The partner's vocabulary for a booking's status is the agreement's, and it
 * has four words in it. Adding a fifth would be a wire change (a v1.x change
 * doc, per invariant 7), and it would be the wrong one anyway: a request is a
 * conversation between a business and somebody at its own front door, and the
 * agent has no verb for it. So `BookingStatus` above **stays at four values**,
 * exactly as it stayed at four when the importer needed `quarantined`
 * (`packages/store/src/schema/enums.ts` makes the same argument about the same
 * column), and `AppointmentStatus` below is the internal vocabulary.
 *
 * What that buys structurally: every partner projection types its status as
 * `BookingStatus`, so a row that reached one carrying `requested` would fail its
 * zod parse at the wire rather than be published. The projections do not rely on
 * that — each of them excludes the value in SQL — but a belt that is a *type*
 * cannot be forgotten by the next person to write a query.
 */
export const AppointmentStatus = z.enum([
    'requested',
    'confirmed',
    'completed',
    'cancelled',
    'no_show',
]);
/**
 * Why a request stopped being one. Recorded on the appointment beside
 * `cancelled_at`, because "she said no" and "nobody answered in time" are
 * different things to tell a guest and different things to count.
 */
export const REQUEST_CANCELLATION_REASONS = ['request_declined', 'request_expired'];
/**
 * What a guest sees her request doing, derived rather than stored.
 *
 * `requested` + `proposed_by = 'staff'` is *proposed*; `cancelled` +
 * `request_expired` is *expired*; `cancelled` + `request_declined` is
 * *declined*. Three of the five states are therefore a pair of columns rather
 * than five enum values, for the reason `photo_album_status` has two: a state
 * that is a function of facts already stored cannot disagree with them.
 */
export const RequestState = z.enum([
    'requested',
    'proposed',
    'confirmed',
    'declined',
    'expired',
    'cancelled',
]);
/** Who last moved a request's time. `staff` is what makes it a *proposal*. */
export const RequestProposedBy = z.enum(['guest', 'staff']);
export function requestStateOf(input) {
    if (input.status === 'requested') {
        return input.proposedBy === 'staff' ? 'proposed' : 'requested';
    }
    if (input.status === 'cancelled') {
        if (input.cancellationReason === 'request_declined')
            return 'declined';
        if (input.cancellationReason === 'request_expired')
            return 'expired';
        return 'cancelled';
    }
    return 'confirmed';
}
export const Booking = z.object({
    booking_id: OpaqueRef,
    status: BookingStatus,
    slot: Slot,
    service_id: OpaqueRef,
    party_size: z.number().int().min(1),
    value_cents: PositiveCents,
    guest_ref: OpaqueRef,
    created_at: IsoDateTime,
});
export const CancelResult = z.object({
    booking_id: OpaqueRef,
    status: z.literal('cancelled'),
    fee_cents: PositiveCents,
    cancelled_at: IsoDateTime,
});
/** Where a booking came from. Every write records it; the reports depend on it. */
export const BookingSource = z.enum(['staff', 'partner', 'import', 'walk_in', 'guest_web']);
/**
 * **Who did it** — the hand behind a `booking.created` / `updated` /
 * `cancelled` event (plan-2026-09-21 X1; status §3 "the confirm double-alert").
 *
 * `source` above is *where the booking came from* and is written once, at
 * creation: a guest's online booking she cancels at the desk is still
 * `guest_web`. The owner-alert loop used to read that column to decide whether
 * to text her, and got the confirm wrong every time — confirming a request
 * emits the ordinary `booking.created`, the row's source is the guest's, and
 * she was told "new booking" about a booking she had just made herself. This
 * is the fact the loop actually needs: **who performed this event**.
 *
 *  - `owner` / `staff` — the seat, by `ref` = its uid. Never a name and never
 *    a telephone number: the event row outlives the seat and is read by
 *    surfaces that hold no grant on `staff_accounts`.
 *  - `guest` — the tenant's own website. No `ref`: the payload already
 *    carries `guest_ref`, and a second spelling would be a second thing to
 *    anonymize.
 *  - `partner` — the provider API, `ref` = the key's integration
 *    (`aiconcierge`), which is a product name and not a person.
 *  - `system` — a sweep, a cascade, an import: nobody's finger. Also what a
 *    door that forgot to say gets, which over-alerts rather than under-alerts.
 *
 * It rides **inside the stored payload and never on the partner wire**: the
 * agreement's webhook body is built field by field in `apps/notifications`
 * (`buildWebhookPayload`) and has no slot for it, exactly as `value_cents` and
 * `created_at` fall off there today.
 */
export const BookingActorKind = z.enum(['owner', 'staff', 'guest', 'partner', 'system']);
export const BookingActor = z.object({
    kind: BookingActorKind,
    /** The seat's uid, or the partner's integration. Never a name, never a phone. */
    ref: z.string().min(1).max(200).optional(),
});
//# sourceMappingURL=booking.js.map